import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Tooltip,
  Paper,
  Grid,
} from '@mui/material';
import { MatchScore } from '@/types';

interface MatchScoreIndicatorProps {
  score: MatchScore;
  showBreakdown?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'linear' | 'circular';
}

const MatchScoreIndicator: React.FC<MatchScoreIndicatorProps> = ({
  score,
  showBreakdown = false,
  size = 'medium',
  variant = 'linear',
}) => {
  const getScoreColor = (value: number) => {
    if (value >= 80) return 'success';
    if (value >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (value: number) => {
    if (value >= 90) return 'Excellent';
    if (value >= 80) return 'Great';
    if (value >= 70) return 'Good';
    if (value >= 60) return 'Fair';
    return 'Poor';
  };

  const scoreComponents = [
    { label: 'Price', value: score.price, key: 'price' },
    { label: 'Location', value: score.location, key: 'location' },
    { label: 'Features', value: score.features, key: 'features' },
    { label: 'Timeline', value: score.timeline, key: 'timeline' },
    { label: 'Quality', value: score.quality, key: 'quality' },
  ];

  if (variant === 'circular') {
    // Simple circular representation using a box with border radius
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Box
          sx={{
            width: size === 'small' ? 60 : size === 'medium' ? 80 : 100,
            height: size === 'small' ? 60 : size === 'medium' ? 80 : 100,
            borderRadius: '50%',
            border: `8px solid`,
            borderColor: `${getScoreColor(score.overall)}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant={size === 'small' ? 'h6' : size === 'medium' ? 'h5' : 'h4'}
            fontWeight="bold"
            color={`${getScoreColor(score.overall)}.main`}
          >
            {score.overall}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getScoreLabel(score.overall)}
          </Typography>
        </Box>
        {showBreakdown && (
          <Tooltip
            title={
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Score Breakdown:
                </Typography>
                {scoreComponents.map((comp) => (
                  <Typography key={comp.key} variant="caption" display="block">
                    {comp.label}: {comp.value}%
                  </Typography>
                ))}
                {score.strengths && score.strengths.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>
                      Strengths:
                    </Typography>
                    {score.strengths.map((strength, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        • {strength}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            }
            arrow
          >
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: 0,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Hover for details
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Linear variant (default)
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="medium">
          Match Score: {score.overall}%
        </Typography>
        <Typography variant="body2" color={`${getScoreColor(score.overall)}.main`}>
          {getScoreLabel(score.overall)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score.overall}
        color={getScoreColor(score.overall)}
        sx={{ height: 10, borderRadius: 1, mb: showBreakdown ? 2 : 0 }}
      />

      {showBreakdown && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Score Breakdown
          </Typography>
          <Grid container spacing={2}>
            {scoreComponents.map((comp) => (
              <Grid item xs={12} sm={6} key={comp.key}>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{comp.label}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {comp.value}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={comp.value}
                    color={getScoreColor(comp.value)}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          {score.strengths && score.strengths.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Strengths
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {score.strengths.map((strength, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{
                      bgcolor: 'success.light',
                      color: 'success.contrastText',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {strength}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {score.weaknesses && score.weaknesses.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Areas to Consider
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {score.weaknesses.map((weakness, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {weakness}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {score.explanation && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {score.explanation}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MatchScoreIndicator;

